import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Printer,
  UserCheck,
  Clock,
  Layers,
  MapPin,
  Loader2,
  CheckCircle2,
  Info,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pressPlateSize, sameReadyPlateSize } from "@apv-erp/status";

const PRINTER_TYPES = ["OFFSET_PRINTER", "FLEXO_PRINTER"];
import { toast } from "sonner";

export interface MountPlateToMachineModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plate: {
    id: string;
    plateId?: string;
    name?: string;
    productName?: string;
    customerName?: string;
    size?: string;
    colors?: any;
    colorNames?: string[];
    activeMachineId?: string;
    activeMachineName?: string;
    mountedBy?: string;
  } | null;
  onSuccess?: (result?: any) => void;
}

export default function MountPlateToMachineModal({
  open,
  onOpenChange,
  plate,
  onSuccess,
}: MountPlateToMachineModalProps) {
  const queryClient = useQueryClient();

  // Query Master Data Machines từ Backend
  const { data: machinesData, isLoading: isLoadingMachines } = useQuery({
    queryKey: ["machines-for-mounting"],
    queryFn: async () => {
      return (trpcClient as any).machines.list.query({ limit: 100 });
    },
    enabled: open,
  });

  const machines = (machinesData?.items || machinesData || []) as any[];

  // Chỉ máy in (offset / flexo) — cùng quy tắc với máy chủ. Trước đây lọc theo tên/nhóm nên máy chưa có nhóm
  // (VD máy nén khí) vẫn hiện và chọn được (FB-R1MC1Y). Máy chưa khai báo khổ kẽm / khác khổ kẽm của bộ kẽm: hiện mờ kèm lý do.
  const pressOptions = machines
    .filter((m: any) => PRINTER_TYPES.includes(String(m.type)))
    .map((m: any) => {
      const size = pressPlateSize(m);
      const reason = !size
        ? "chưa khai báo khổ kẽm"
        : plate?.size && !sameReadyPlateSize(plate.size, size)
          ? `khổ ${size}, khác khổ bộ kẽm`
          : null;
      return { id: m.id || m.machineId, label: `${m.name || m.machineId}${m.machineId ? ` (${m.machineId})` : ""}`, size, reason };
    })
    .sort((a: any, b: any) => Number(!!a.reason) - Number(!!b.reason));
  const compatiblePresses = pressOptions.filter((o: any) => !o.reason);

  const [machineId, setMachineId] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [shift, setShift] = useState("CA_1");
  const [rackPosition, setRackPosition] = useState("Máng Kẽm Số 1");
  const [notes, setNotes] = useState("");
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Phân tích danh sách màu của bài kẽm
  const availableColors = React.useMemo(() => {
    if (!plate) return ["Cyan (C)", "Magenta (M)", "Yellow (Y)", "Black (K)"];
    if (Array.isArray(plate.colors) && plate.colors.length > 0) {
      return plate.colors;
    }
    if (Array.isArray(plate.colorNames) && plate.colorNames.length > 0) {
      return plate.colorNames;
    }
    return ["Cyan (C)", "Magenta (M)", "Yellow (Y)", "Black (K)"];
  }, [plate]);

  useEffect(() => {
    if (open && plate) {
      // Prefill machine nếu đã có
      // Mặc định: máy đang giữ kẽm nếu hợp khổ, không thì máy in hợp khổ đầu tiên; không có thì để trống (không chọn bừa máy đầu danh sách)
      const current = compatiblePresses.find((o: any) => o.id === plate.activeMachineId);
      setMachineId(current?.id || compatiblePresses[0]?.id || "");
      setOperatorName(plate.mountedBy || "Trưởng Máy Offset");
      setShift("CA_1");
      setRackPosition("Máng Kẽm Đầu Máy");
      setNotes("");
      setSelectedColors([...availableColors]);
    }
  }, [open, plate, compatiblePresses.length]);

  const toggleColor = (color: string) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  const selectAllColors = () => {
    setSelectedColors([...availableColors]);
  };

  const deselectAllColors = () => {
    setSelectedColors([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plate) return;

    if (!machineId) {
      toast.error("Vui lòng chọn máy in đích để lắp kẽm");
      return;
    }

    if (!operatorName.trim()) {
      toast.error("Vui lòng nhập tên thợ trưởng máy ký nhận kẽm");
      return;
    }

    if (selectedColors.length === 0) {
      toast.error("Vui lòng tick chọn ít nhất 1 lá kẽm phân màu bàn giao");
      return;
    }

    const selectedMachine = machines.find(
      (m: any) => (m.id || m.machineId) === machineId
    );
    const machineName = selectedMachine?.name || "Máy In Offset";

    setIsSubmitting(true);
    try {
      const result = await (trpcClient as any).technical.plates.mountToMachine.mutate({
        id: plate.id,
        machineId,
        machineName,
        operatorName: operatorName.trim(),
        shift,
        colors: selectedColors,
        rackPosition: rackPosition.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      toast.success(
        `Đã cấp phát bản kẽm ${plate.plateId || ""} lên ${machineName} thành công!`
      );

      await queryClient.invalidateQueries({ queryKey: ["plate", plate.id] });
      await queryClient.invalidateQueries({ queryKey: ["plates"] });
      await queryClient.invalidateQueries({ queryKey: ["plate-usage-history"] });

      onSuccess?.(result);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi cấp phát bản kẽm lên máy in");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!plate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Printer className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>
                Cấp Phát & Lắp Bản Kẽm Lên Máy In Offset
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Ghi nhận điều phối kẽm CTP sang xưởng in, xác nhận thợ máy nhận kẽm theo phân màu
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Thông tin bài kẽm */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs grid grid-cols-2 gap-2">
            <div>
              <span className="text-slate-500">Mã bài kẽm:</span>{" "}
              <span className="font-mono font-bold text-slate-800">{plate.plateId || plate.id}</span>
            </div>
            <div>
              <span className="text-slate-500">Khách hàng:</span>{" "}
              <span className="font-medium text-slate-800">{plate.customerName || "—"}</span>
            </div>
            <div className="col-span-2">
              <span className="text-slate-500">Tên bài in / Sản phẩm:</span>{" "}
              <span className="font-semibold text-slate-900">
                {plate.productName || plate.name || "Bản kẽm Offset"}
              </span>
            </div>
            {plate.size && (
              <div>
                <span className="text-slate-500">Khổ kẽm:</span>{" "}
                <span className="font-mono font-medium text-blue-600">{plate.size}</span>
              </div>
            )}
            <div>
              <span className="text-slate-500">Tổng số màu:</span>{" "}
              <span className="font-bold text-slate-800">{availableColors.length} màu</span>
            </div>
          </div>

          {/* Form chọn máy và người nhận */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Printer className="w-3.5 h-3.5 text-blue-500" />
                Máy In Offset Nhận Kẽm <span className="text-rose-500">*</span>
              </Label>
              {isLoadingMachines ? (
                <div className="h-8 flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 className="w-3 h-3 animate-spin" /> Đang tải danh sách máy...
                </div>
              ) : (
                <Select value={machineId} onValueChange={setMachineId}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Chọn máy in..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pressOptions.map((o: any) => (
                      <SelectItem key={o.id} value={o.id} disabled={!!o.reason} className="text-xs" data-testid="mount-press-option">
                        {o.label}
                        {o.size && !o.reason ? ` — khổ ${o.size}` : ""}
                        {o.reason ? ` — ${o.reason}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!isLoadingMachines && compatiblePresses.length === 0 && (
                <p className="text-[11px] text-amber-700" data-testid="mount-no-press-hint">
                  Chưa có máy in nào cùng khổ kẽm — khai báo khổ kẽm ở Xưởng sản xuất → Thiết bị SX.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <UserCheck className="w-3.5 h-3.5 text-emerald-500" />
                Thợ Trưởng Máy Nhận Kẽm <span className="text-rose-500">*</span>
              </Label>
              <Input
                placeholder="VD: Nguyễn Văn Thợ (Trưởng máy)"
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                className="h-8 text-xs"
                required
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-amber-500" />
                Ca / Kíp Sản Xuất
              </Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Chọn ca..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CA_1" className="text-xs">Ca 1 (06:00 - 14:00)</SelectItem>
                  <SelectItem value="CA_2" className="text-xs">Ca 2 (14:00 - 22:00)</SelectItem>
                  <SelectItem value="CA_3" className="text-xs">Ca 3 (22:00 - 06:00)</SelectItem>
                  <SelectItem value="HANH_CHINH" className="text-xs">Giờ hành chính</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                Vị Trí Máng Kẽm / Giá Gá
              </Label>
              <Input
                placeholder="VD: Máng 1 / Giá kẽm đầu máy"
                value={rackPosition}
                onChange={(e) => setRackPosition(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          </div>

          {/* Phân màu kẽm bàn giao */}
          <div className="space-y-2 p-3 bg-slate-50/70 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 text-indigo-500" />
                Danh Sách Lá Kẽm Bàn Giao ({selectedColors.length}/{availableColors.length}) <span className="text-rose-500">*</span>
              </Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllColors}
                  className="text-[11px] text-blue-600 hover:underline"
                >
                  Chọn tất cả
                </button>
                <span className="text-slate-300">|</span>
                <button
                  type="button"
                  onClick={deselectAllColors}
                  className="text-[11px] text-slate-500 hover:underline"
                >
                  Bỏ chọn
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              {availableColors.map((colorName: string) => {
                const isChecked = selectedColors.includes(colorName);
                return (
                  <div
                    key={colorName}
                    onClick={() => toggleColor(colorName)}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer select-none text-xs transition-colors ${
                      isChecked
                        ? "bg-blue-50 border-blue-300 text-blue-900 font-medium"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleColor(colorName)}
                      className="pointer-events-none"
                    />
                    <span className="break-words whitespace-normal">{colorName}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ghi chú */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              Ghi Chú Bàn Giao Kỹ Thuật
            </Label>
            <Textarea
              rows={2}
              placeholder="VD: Kiểm tra kỹ ngàm kẽm trước khi xiết ốc, bài in nền bệt cần lưu ý độ dày mực..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="text-xs resize-none"
            />
          </div>

          <DialogFooter className="pt-2 border-t border-slate-100 flex items-center justify-between sm:justify-between">
            <div className="flex items-center gap-1.5 text-slate-400 text-xs">
              <Info className="w-3.5 h-3.5" />
              <span>Chuyển trạng thái sang "Đang Sử Dụng (IN_USE)" & ghi sổ lịch sử</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Đang bàn giao...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                    Xác Nhận Lắp Máy In
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
